/* EventsTeaserSection.jsx — Sezione hero dinamica eventi in programma.
 *
 * Sostituisce/integra il blocco "73% sottoassicurato" della homepage.
 * Mostra l'evento attivo piu' imminente (o carousel se >1).
 *
 * Backend feeds:
 *  - GET /api/public/homepage/config       (hero_mode, teaser flags)
 *  - GET /api/public/eventi/in-programma   (lista eventi futuri)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Calendar, MapPin, ArrowRight, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EventsTeaserSection({ config }) {
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/public/eventi/in-programma?limit=${config?.events_teaser_max_items || 3}`)
      .then((r) => setEventi(r.data.items || []))
      .catch(() => setEventi([]))
      .finally(() => setLoading(false));
  }, [config?.events_teaser_max_items]);

  if (loading || !eventi.length) return null;

  const ev = eventi[0];
  const titleTpl = config?.events_teaser_title_template
    || "Vieni a trovarci a {titolo}";
  const eventoLabel = ev.titolo.split("×")[0].trim();
  const heroTitle = titleTpl.replace("{titolo}", eventoLabel);

  return (
    <>
      {/* FASE 4.2.11.2 hotfix: rimosso eyebrow pill "EVENTI COLUCCI" + gradient
          psichedelico su nome evento. Titolo coerente con brand palette,
          dimensione tipografica in scala con altri hero (no 7xl). */}
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 leading-[1.05] mb-4"
        data-testid="hero-events-title"
      >
        {heroTitle}
      </motion.h1>

      {/* Save the date accent line — colore sand/amber pacato */}
      {ev.data_inizio && (
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="text-sm font-semibold uppercase tracking-wider text-amber-700 mb-4"
          data-testid="hero-events-save-the-date"
        >
          Save the date · dal {formatDateIT(ev.data_inizio)}
          {ev.data_fine && ev.data_fine !== ev.data_inizio
            && ` al ${formatDateIT(ev.data_fine)}`}
        </motion.p>
      )}

      {ev.sottotitolo && (
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-base sm:text-lg text-zinc-500 leading-relaxed mb-6 max-w-lg"
        >
          {ev.sottotitolo}
        </motion.p>
      )}

      {/* Mini event card details */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="flex flex-wrap gap-3 mb-8 text-sm"
        data-testid="hero-events-meta"
      >
        {ev.data_inizio && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-zinc-200">
            <Calendar className="w-3.5 h-3.5 text-[#163321]" />
            {formatDateIT(ev.data_inizio)}
            {ev.data_fine && ev.data_fine !== ev.data_inizio
              && ` — ${formatDateIT(ev.data_fine)}`}
          </span>
        )}
        {ev.ora_inizio && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-zinc-200">
            <Clock className="w-3.5 h-3.5 text-[#163321]" />
            {ev.ora_inizio}{ev.ora_fine && ` - ${ev.ora_fine}`}
          </span>
        )}
        {ev.luogo && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-zinc-200">
            <MapPin className="w-3.5 h-3.5 text-[#D97B58]" />
            {ev.luogo}{ev.citta && `, ${ev.citta}`}
          </span>
        )}
      </motion.div>

      {/* CTA principali — FASE 4.2.11.3: "Preventivo Gratuito" primary brand */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.4 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <Link
          to="/preventivo-auto"
          className="inline-flex items-center justify-center gap-2 px-8 h-14 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-xl shadow-[#163321]/25"
          data-testid="hero-cta-preventivo"
        >
          Preventivo Gratuito <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to={`/eventi/${ev.slug}`}
          className="inline-flex items-center justify-center gap-2 px-8 h-14 bg-white text-zinc-900 text-sm font-semibold rounded-full border border-zinc-200 hover:border-[#163321] hover:bg-[#163321]/5 transition-all duration-200"
          data-testid="hero-events-cta-iscriviti"
        >
          Iscriviti all'evento <ArrowRight className="w-4 h-4" />
        </Link>
      </motion.div>

      {/* Carousel mini se >1 evento */}
      {eventi.length > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 pt-6 border-t border-zinc-200"
          data-testid="hero-events-more"
        >
          <p className="text-xs uppercase tracking-wider text-zinc-400 font-bold mb-3">
            Altri eventi in programma
          </p>
          <div className="flex flex-col gap-2">
            {eventi.slice(1).map((e) => (
              <Link
                key={e.slug}
                to={`/eventi/${e.slug}`}
                className="flex items-center justify-between p-3 rounded-xl bg-white border border-zinc-200 hover:border-[#163321] hover:bg-[#163321]/5 transition-all group"
                data-testid={`hero-event-other-${e.slug}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-zinc-900 truncate">{e.titolo}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {e.data_inizio && formatDateIT(e.data_inizio)} · {e.luogo}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-[#163321] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </>
  );
}

function formatDateIT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}
